'use client';

import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';

import { Product } from '../../../services/types/product';
import { getImageAlt, getImageUrl } from '../../../services/utils/image';

export interface ProductCardProps {
  product: Product;
  variant?: 'default' | 'featured' | 'compact';
}

const ProductCard = ({ product, variant = 'default' }: ProductCardProps) => {
  const getMainImage = () => {
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    return null;
  };

  const mainImage = getMainImage();
  const imageUrl = getImageUrl(mainImage);
  const imageAlt = getImageAlt(mainImage);

  const cardStyles = {
    default: {
      height: '100%',
      maxWidth: 500,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
      },
    },
    featured: {
      height: '100%',
      maxWidth: 500,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
      },
    },
    compact: {
      height: '100%',
      maxWidth: 500,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      },
    },
  };

  const mediaStyles = {
    default: {
      height: 240,
      position: 'relative',
      backgroundColor: '#f5f5f5',
    },
    featured: {
      height: 280,
      position: 'relative',
      backgroundColor: '#f5f5f5',
    },
    compact: {
      height: 180,
      position: 'relative',
      backgroundColor: '#f5f5f5',
    },
  };

  const contentStyles = {
    default: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
    },
    featured: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '24px',
    },
    compact: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
    },
  };

  return (
    <Link
      href={`/products/${product.slug}`}
      style={{ textDecoration: 'none' }}
      aria-label={`View details for ${product.name}`}
    >
      <Card sx={cardStyles[variant]}>
        <CardMedia sx={mediaStyles[variant]}>
          {mainImage ? (
            <Image
              src={imageUrl}
              alt={imageAlt}
              fill
              style={{ objectFit: 'cover' }}
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                color: '#999',
              }}
            >
              <Typography variant='body2'>Không có hình ảnh</Typography>
            </Box>
          )}
          {!product.isActive && (
            <Chip
              label='Hết hàng'
              color='error'
              size='small'
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                backgroundColor: 'rgba(244, 67, 54, 0.9)',
                color: 'white',
              }}
            />
          )}
        </CardMedia>
        <CardContent sx={contentStyles[variant]}>
          <Typography
            variant={variant === 'featured' ? 'h6' : 'subtitle1'}
            component='h3'
            sx={{
              fontWeight: 600,
              color: '#1A1A1A',
              marginBottom: 1,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.name}
          </Typography>

          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              marginBottom: 2,
              flexGrow: 1,
              display: '-webkit-box',
              WebkitLineClamp: variant === 'compact' ? 2 : 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
            }}
            dangerouslySetInnerHTML={{
              __html:
                product.description.replace(/<[^>]*>/g, '').substring(0, 120) +
                '...',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant={variant === 'featured' ? 'h6' : 'subtitle1'}
              sx={{
                fontWeight: 700,
              }}
            >
              {'Xem chi tiết ->'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProductCard;
